import { Component, HostBinding, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Invite } from '@models/businesses';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import LogRocket from 'logrocket';
import { filter, take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import { Observable } from 'rxjs';
import { UrlResult, UrlService } from 'src/app/shared/services/url/url.service';
import { checkForDisposableEmail } from 'src/app/shared/core/util/lookForDisposableEmail';
import { AuthUser } from '@state/auth/auth.models';
import { NavigationState } from '@state/navigation/navigation.state';
import { BusinessSignUpRequestService } from '@services/closer-api/businessSignUpRequests.service';

enum USER_CREDENTIALS {
	EMAIL = 'email',
	PASSWORD = 'password',
	CONFIRM = 'confirm',
	SUBSCRIBE_TO_NEWSLETTER = 'subscribeToNewsletter',
	SIGNUP_AGREEMENT = 'signupAgreement',
}

@Component({
	selector: 'app-register',
	templateUrl: './register.component.html',
	standalone: false,
})
export class RegisterComponent implements OnInit {
	public USER_CREDENTIALS = USER_CREDENTIALS;
	public error = false;
	public legalUrl = UrlService.Legal();
	public inviteId = this.activatedRoute.snapshot.paramMap.get('inviteId');
	public signUpRequestId =
		this.activatedRoute.snapshot.queryParamMap.get('requestId');
	public ICON = ICON;
	public loading: boolean;
	public displayName: string;
	public authForm = this.fb.group({
		[USER_CREDENTIALS.EMAIL]: this.fb.control(
			{
				value: '',
				disabled: true,
			},
			[Validators.required, Validators.email],
		),
		[USER_CREDENTIALS.PASSWORD]: this.fb.control('', [Validators.required]),
		[USER_CREDENTIALS.CONFIRM]: this.fb.control('', [Validators.required]),
		[USER_CREDENTIALS.SIGNUP_AGREEMENT]: this.fb.control(false, [
			Validators.requiredTrue,
		]),
	});
	@Select(AuthState.getLoading) loading$: Observable<boolean>;
	@Select(AuthState.getInvite) invite$: Observable<Invite>;
	@SelectSnapshot(AuthState.getLoggedInUser) user: AuthUser;
	@Select(NavigationState.getQueryParams) queryParams$: Observable<{
		[id: string]: string;
	}>;

	constructor(
		private fb: FormBuilder,
		private authDispatch: AuthDispatch,
		private activatedRoute: ActivatedRoute,
		private businessSignUpRequestService: BusinessSignUpRequestService,
	) {}

	@HostBinding('class.w-full.h-full') get noError() {
		return this.error;
	}

	async ngOnInit(): Promise<void> {
		this.authForm.addValidators(this.sameEmailValidator.bind(this));
		const email = this.activatedRoute.snapshot.queryParamMap.get('email');
		this.loading = true;

		if (this.user) {
			this.authDispatch.LogOut();
		}

		if (!this.inviteId && !this.signUpRequestId) {
			this.loading = false;
			this.error = true;
			return;
		}

		if (this.inviteId) {
			this.authDispatch.GetInvite(this.inviteId, email);

			this.invite$
				.pipe(
					filter((invite) => !!invite),
					take(1),
				)
				.subscribe((invite) => {
					if (invite.businessId && invite.email) {
						// valid invitation
						this.email.setValue(invite.email);
					} else {
						// a missing or malformed invitation
						this.error = true;
						LogRocket.captureMessage(
							`That invitation could not be found. InviteID: ${this.inviteId}.`,
							{
								tags: {
									feature: 'register',
									section: 'invitation',
								},
								extra: {
									invite: JSON.stringify(invite),
								},
							},
						);
					}
					this.loading = false;
				});
		} else {
			try {
				const request = await this.businessSignUpRequestService
					.get(this.signUpRequestId)
					.pipe(take(1))
					.toPromise();
				this.authDispatch.SetSignUpRequest(request);

				if (request) {
					this.email.setValue(request.email);
				}
			} catch (err) {
				console.log('Error getting request', err);
				this.error = true;
			} finally {
				this.loading = false;
			}
		}
	}

	get loginUrl(): UrlResult {
		return UrlService.Login();
	}

	get email(): AbstractControl {
		return this.authForm.controls[USER_CREDENTIALS.EMAIL];
	}

	public registerUser(): void {
		if (this.authForm.invalid) {
			this.authForm.markAllAsTouched();
			return;
		}

		const { email, password } = this.authForm.getRawValue();

		if (checkForDisposableEmail(email)) {
			this.email.setErrors({ disposableEmail: true });
		} else {
			this.authDispatch.SignUpEmailAndPassword(email, password);
		}
	}

	private sameEmailValidator = (
		control: typeof this.authForm,
	): { [key: string]: boolean } => {
		if (control.controls[USER_CREDENTIALS.CONFIRM].pristine) return null;
		const { password, confirm } = control.value;
		control.controls[USER_CREDENTIALS.CONFIRM].setErrors(
			password !== confirm ? { passwordsMatch: true } : null,
		);
	};
}
